import React, { useState, useMemo, useEffect } from 'react'
import { Popup, Search, Cell } from 'react-vant'
import { useTranslation } from 'react-i18next'
import { getCountryPhoneCodes, type CountryCode } from '@/utils/countryData'

interface CountryCodeSelectorProps {
    value: string
    onChange: (dialCode: string) => void
    placeholder?: string
}

const CountryCodeSelector: React.FC<CountryCodeSelectorProps> = ({
    value,
    onChange,
    placeholder = '+86'
}) => {
    const { t } = useTranslation()
    const [visible, setVisible] = useState(false)
    const [searchText, setSearchText] = useState('')
    const [countryCodes, setCountryCodes] = useState<CountryCode[]>([])

    // 加载国家区号列表
    useEffect(() => {
        getCountryPhoneCodes().then(codes => {
            setCountryCodes(codes)
        })
    }, [])

    // 获取选中的区号信息
    const selectedCode = useMemo(() => {
        return countryCodes.find(item => item.dialCode === value)
    }, [countryCodes, value])

    // 过滤区号列表
    const filteredCodes = useMemo(() => {
        if (!searchText.trim()) {
            return countryCodes
        }
        const searchLower = searchText.toLowerCase()
        return countryCodes.filter(item =>
            item.name.toLowerCase().includes(searchLower) ||
            item.dialCode.includes(searchLower) ||
            item.code.toLowerCase().includes(searchLower)
        )
    }, [countryCodes, searchText])

    // 处理选择
    const handleSelect = (item: CountryCode) => {
        onChange(item.dialCode)
        setVisible(false)
        setSearchText('')
    }

    return (
        <>
            <div
                onClick={() => setVisible(true)}
                className="w-full px-4 h-12 border-1 border-[#EAECF0] border-solid rounded-2.5 bg-white cursor-pointer flex items-center justify-between"
            >
                <span className="text-base text-[#1D2129] mr-2">
                    {selectedCode ? selectedCode.dialCode : placeholder}
                </span>
                <img src="/icon/select-down.svg" alt="" />
            </div>

            <Popup
                visible={visible}
                onClose={() => {
                    setVisible(false)
                    setSearchText('')
                }}
                position="bottom"
                round
                style={{ height: '70%' }}
            >
                <div className="flex flex-col h-full">
                    {/* 标题 */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                        <span
                            className="text-sm text-gray-500 cursor-pointer"
                            onClick={() => {
                                setVisible(false)
                                setSearchText('')
                            }}
                        >
                            {t('取消')}
                        </span>
                        <span className="text-base font-medium">{t('选择国家区号')}</span>
                        <span className="w-12"></span>
                    </div>

                    {/* 搜索框 */}
                    <Search
                        value={searchText}
                        onChange={setSearchText}
                        placeholder={t('搜索国家或区号')}
                        shape="round"
                    />

                    {/* 区号列表 */}
                    <div className="flex-1 overflow-y-auto">
                        {filteredCodes.length > 0 ? (
                            filteredCodes.map((item) => (
                                <Cell
                                    key={item.name}
                                    title={
                                        <div className="flex items-center justify-between w-full">
                                            <span>{item.name}</span>
                                            <span className="text-gray-500 ml-2">{item.dialCode}</span>
                                        </div>
                                    }
                                    clickable
                                    onClick={() => handleSelect(item)}
                                    className={value === item.dialCode ? 'bg-blue-50' : ''}
                                    rightIcon={value === item.dialCode ? <span className="text-blue-600">✓</span> : null}
                                />
                            ))
                        ) : (
                            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                                <div className="text-sm">{t('未找到匹配的区号')}</div>
                            </div>
                        )}
                    </div>
                </div>
            </Popup>
        </>
    )
}

export default CountryCodeSelector
